import { useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { Loader } from '../components/loader';
import { QuestionView } from '../components/question-view';
import {
  useTestAccess,
  useStartTest,
  useCompleteTest,
  TestAttempt,
} from '../hooks/use-test';
import { TestBlocked } from '../components/test-blocked';
import { TestLoadError } from '../components/test-load-error';
import { useWorkspace } from '../context/workspace-context';
import { Question, QuestionAttempt } from '../interfaces/course';
import { TEST_REASON } from '../constants/test-reason';
import { Sentry } from '../lib/sentry';
import { TIMEOUT_PAGE_PATH } from './test-time-out';

const MILLISECONDS_PER_SECOND = 1000;

function addLevelAndCourse(
  attempts: QuestionAttempt[],
  questions: Question[],
): TestAttempt[] {
  return attempts.map((attempt) => {
    const matchingQuestion = questions.find(
      (question) => parseInt(question.id, 10) === attempt.question_id,
    );

    if (
      !matchingQuestion ||
      !matchingQuestion.level_id ||
      !matchingQuestion.course_id
    ) {
      throw new Error(
        `Missing question data for attempt ${attempt.question_id}`,
      );
    }

    return {
      question_id: attempt.question_id,
      level_id: matchingQuestion.level_id,
      course_id: matchingQuestion.course_id,
      is_correct: attempt.is_correct,
      answered_at: attempt.answered_at,
    };
  });
}

function buildWrongAttempts(
  questions: Question[],
  answeredIds: Set<number>,
): TestAttempt[] {
  const now = new Date().toISOString();
  const wrongAttempts: TestAttempt[] = [];

  for (const question of questions) {
    const questionId = parseInt(question.id, 10);
    if (answeredIds.has(questionId)) continue;
    if (!question.level_id || !question.course_id) continue;

    wrongAttempts.push({
      question_id: questionId,
      level_id: question.level_id,
      course_id: question.course_id,
      is_correct: false,
      answered_at: now,
    });
  }

  return wrongAttempts;
}

const TestPage = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const access = useTestAccess(currentWorkspace?.id ?? 0);
  const startTest = useStartTest();
  const completeTestMutation = useCompleteTest(startTest.data?.sessionId ?? 0);

  useEffect(() => {
    if (!currentWorkspace) return;
    if (!access.data?.canStart) return;
    if (startTest.data) return;
    if (startTest.isPending) return;

    startTest.mutate(currentWorkspace.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, access.data?.canStart]);

  const isSubmittingRef = useRef(false);
  const hasExitedRef = useRef(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number | undefined>(
    undefined,
  );

  // Intercept browser back so the user gets the same confirm modal
  // as the in-app back button instead of silently navigating away.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !hasExitedRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  const isBlocked = blocker.state === 'blocked';

  const dismissBlockedExit = () => {
    if (blocker.state === 'blocked') blocker.reset();
  };

  const leaveTestPage = (destination: string) => {
    hasExitedRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }
    navigate(destination);
  };

  useEffect(() => {
    const timeLimit = startTest.data?.timeLimitSeconds;
    if (timeLimit == null) return;

    const deadline = Date.now() + timeLimit * MILLISECONDS_PER_SECOND;

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((deadline - Date.now()) / MILLISECONDS_PER_SECOND),
      );
      setSecondsRemaining(remaining);
    };

    tick();
    const intervalId = window.setInterval(tick, MILLISECONDS_PER_SECOND);
    return () => window.clearInterval(intervalId);
  }, [startTest.data?.timeLimitSeconds]);

  const complete = async (attempts: QuestionAttempt[]) => {
    if (!startTest.data) return false;
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;

    try {
      await completeTestMutation.mutateAsync({
        attempts: addLevelAndCourse(attempts, startTest.data.questions),
        reason: TEST_REASON.COMPLETE,
      });
      // QuestionView will navigate after this returns true.
      // Mark the ref now so the blocker lets that navigation through.
      hasExitedRef.current = true;
      return true;
    } catch (error) {
      Sentry.captureException(error);
      isSubmittingRef.current = false;
      await showBlockedPageIfNoLongerAllowed();
      return false;
    }
  };

  // The backend re-checks the maximum when a test is submitted. If another
  // tab finished first, this submit is refused; the fresh access answer
  // then says max reached and the page shows the blocked screen.
  const showBlockedPageIfNoLongerAllowed = async () => {
    const refreshed = await access.refetch();
    if (refreshed.data?.canStart !== false) return;
    hasExitedRef.current = true;
  };

  const exit = async (attempts: QuestionAttempt[]) => {
    if (!startTest.data) {
      leaveTestPage('/courses');
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const answeredIds = new Set(attempts.map((attempt) => attempt.question_id));
    const allAttempts = [
      ...addLevelAndCourse(attempts, startTest.data.questions),
      ...buildWrongAttempts(startTest.data.questions, answeredIds),
    ];

    try {
      await completeTestMutation.mutateAsync({
        attempts: allAttempts,
        reason: TEST_REASON.EXIT,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      leaveTestPage('/courses');
    }
  };

  const timeOut = async (attempts: QuestionAttempt[]) => {
    if (!startTest.data) {
      leaveTestPage(TIMEOUT_PAGE_PATH);
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const answeredIds = new Set(attempts.map((attempt) => attempt.question_id));
    const allAttempts = [
      ...addLevelAndCourse(attempts, startTest.data.questions),
      ...buildWrongAttempts(startTest.data.questions, answeredIds),
    ];

    try {
      await completeTestMutation.mutateAsync({
        attempts: allAttempts,
        reason: TEST_REASON.TIMEOUT,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      leaveTestPage(TIMEOUT_PAGE_PATH);
    }
  };

  if (!currentWorkspace || access.isPending || startTest.isPending) {
    return <Loader />;
  }

  if (access.error) {
    return <TestLoadError message={access.error.message} />;
  }

  if (access.data && !access.data.canStart) {
    return <TestBlocked access={access.data} />;
  }

  if (startTest.error) {
    return <TestLoadError message={startTest.error.message} />;
  }

  if (!startTest.data || startTest.data.questions.length === 0) {
    return (
      <div className="flex flex-col min-h-screen max-w-lg mx-auto bg-bg-main p-6">
        <h1 className="text-xl font-bold text-title mb-4">No Questions</h1>
        <p className="text-subheading">
          There are no questions available for a test right now.
        </p>
        <button
          onClick={() => navigate('/courses')}
          className="mt-4 bg-accent-purple text-white px-4 py-2 rounded-lg"
        >
          Back to Courses
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen max-w-lg mx-auto bg-bg-main">
      <QuestionView
        questions={startTest.data.questions}
        backUrl="/courses"
        exitConfirm={{
          title: 'Exit test?',
          description:
            'Please note you will be exiting this test. All unanswered questions will be marked incorrect for this attempt.',
        }}
        shouldOpenExitModal={isBlocked}
        onExitModalDismiss={dismissBlockedExit}
        onComplete={complete}
        onExit={exit}
        onTimeOut={timeOut}
        secondsRemaining={secondsRemaining}
      />
    </div>
  );
};

export default TestPage;
